import { useEffect, useMemo, useRef } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { SenderProfile, isTemplate } from 'domains/content/types';
import Button from 'domains/core/components/Button';
import Select from 'domains/core/components/Select';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import WarningSnackbar from 'domains/core/components/Snackbars/WarningSnackbar';
import ContentIcon from 'domains/core/components/SvgIcons/ContentIcon';
import TextField from 'domains/core/components/TextField';
import useSendTestEmail from 'hooks/mutations/useSendTestEmail';
import { RowDisplayCondition } from 'hooks/queries/useDisplayConditions';
import useSenderProfiles from 'hooks/queries/useSenderProfiles';
import useTemplate from 'hooks/queries/useTemplate';
import theme from 'theme';

import DisplayConditionOptionMultiSelect from './DisplayConditionOptionMultiSelect';

type BeeEditorTestSendDialogProps = {
    handleClose: () => void;
    isOpen: boolean;
    templateId: number;
    displayConditions?: RowDisplayCondition[];
    hasDynamicContent?: boolean;
};

export type DisplayConditionOption = {
    id: string;
    label: string;
    description: string;
};

type FormData = {
    templateId: number;
    subjectLine: string;
    preHeader: string;
    senderProfileId: number;
    toAddresses: string;
    displayConditions: DisplayConditionOption[];
};

const BeeEditorTestSendDialog = ({
    handleClose,
    isOpen,
    templateId,
    displayConditions = [],
    hasDynamicContent,
}: BeeEditorTestSendDialogProps) => {
    const location = useLocation();
    const isDefaultTemplate = location.search === '?id=default';

    const { data: senderProfiles, isLoading: isSenderProfilesLoading } = useSenderProfiles();

    // Convert RowDisplayCondition to DisplayConditionOption format.
    // `condition.id` is numeric; stringifying it matches the
    // `displayConditions: Record<string, boolean>` shape the backend expects.
    // Memoized so the seeding effect below only fires when the upstream
    // condition list actually changes, not on every render.
    const displayConditionOptions: DisplayConditionOption[] = useMemo(
        () =>
            displayConditions.map((condition) => ({
                id: condition.id.toString(),
                label: condition.label,
                description: condition.description,
            })),
        [displayConditions]
    );

    const {
        error: sendTestEmailError,
        isError: isSendTestEmailError,
        isLoading: isSendTestEmailLoading,
        isSuccess: isSendTestEmailSuccess,
        mutate: sendTestEmail,
        reset: resetSendTestEmail,
    } = useSendTestEmail();

    const {
        control,
        formState: { isValid },
        handleSubmit,
        setValue,
    } = useForm<FormData>({
        defaultValues: {
            senderProfileId: null,
            toAddresses: '',
            subjectLine: '',
            preHeader: '',
            displayConditions: [],
        },
        // onChange so the Send button enables as soon as required fields are populated,
        // without requiring the user to blur each field first.
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    // Refs gate one-time seeding so reopening the dialog does NOT overwrite
    // values the user has already edited (COC-4287). Both reset when templateId
    // changes so a different template gets its own first-open seed.
    const hasSeededTemplateRef = useRef(false);
    const hasSeededConditionsRef = useRef(false);
    useEffect(() => {
        hasSeededTemplateRef.current = false;
        hasSeededConditionsRef.current = false;
    }, [templateId]);

    const { refetch: refetchTemplate } = useTemplate({
        templateId: `${templateId}`,
        reactQueryOptions: {
            enabled: isOpen,
            onSuccess: (template) => {
                if (!isTemplate(template) || !template) {
                    return;
                }
                // Seed template defaults exactly once per templateId so a
                // Cancel + reopen preserves the user's edits to these fields.
                if (hasSeededTemplateRef.current) {
                    return;
                }
                const { senderProfileId, subjectLine, preHeader } = template;
                if (senderProfileId != null) {
                    setValue('senderProfileId', senderProfileId, { shouldValidate: true });
                }
                if (subjectLine != null) {
                    setValue('subjectLine', subjectLine, { shouldValidate: true });
                }
                if (preHeader != null) {
                    setValue('preHeader', preHeader, { shouldValidate: true });
                }
                hasSeededTemplateRef.current = true;
            },
        },
    });

    useEffect(() => {
        if (isOpen) {
            resetSendTestEmail();
            refetchTemplate();
        }
    }, [isOpen, refetchTemplate, resetSendTestEmail]);

    // Pre-select every used display condition the first time the dialog opens
    // with options available. Without this, an untouched multi-select submits
    // every condition as `false`, causing the backend to strip every
    // {% if conditional_field_X %}...{% endif %} block from the test email.
    useEffect(() => {
        if (isOpen && !hasSeededConditionsRef.current && displayConditionOptions.length > 0) {
            setValue('displayConditions', displayConditionOptions);
            hasSeededConditionsRef.current = true;
        }
    }, [isOpen, displayConditionOptions, setValue]);

    const onSubmit = (data: FormData) => {
        const delimiters = /[,]+/; // comma
        const splitAddresses = data.toAddresses.split(delimiters);
        const trimSplitAddresses = splitAddresses.map((address) => address.trim());

        // Transform display conditions to the required format. Coerced to a
        // strict boolean so the payload matches Record<string, boolean>.
        const displayConditionsPayload: Record<string, boolean> = {};
        displayConditionOptions.forEach((option) => {
            const isSelected = !!data.displayConditions?.some((selected) => selected.id === option.id);
            displayConditionsPayload[option.id] = isSelected;
        });

        sendTestEmail({
            preheader: data.preHeader,
            subjectLine: data.subjectLine,
            templateId,
            senderProfileId: data.senderProfileId,
            toAddresses: trimSplitAddresses,
            displayConditions: displayConditionsPayload,
        });
        handleClose();
    };

    const mapSenderProfilesToSelectValues = (profiles: SenderProfile[]) =>
        profiles.map((profile) => ({
            value: profile.id,
            label: profile.from_address,
        }));

    return (
        <>
            {isDefaultTemplate && isOpen && (
                <WarningSnackbar warningMessage="Please make changes to the template before sending a test email." />
            )}
            {isSendTestEmailError && <ErrorSnackbar errorMessage={sendTestEmailError.message} />}
            {isSendTestEmailSuccess && !isOpen && (
                <SuccessSnackbar
                    successMessage="You've successfully sent a test email."
                    data-testid="send-test-success-alert"
                />
            )}
            <Dialog maxWidth="sm" fullWidth open={isOpen} onClose={handleClose} data-testid="send-test-panel">
                <DialogTitle id="upload-contacts-dialog">
                    <Stack alignItems="center" direction="row" spacing={2}>
                        <ContentIcon sx={{ fontSize: theme.spacing(5) }} />
                        <Typography variant="h6">send test email</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ px: 0 }}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack sx={{ px: 4, py: 2 }} rowGap={4}>
                            <Controller
                                control={control}
                                name="senderProfileId"
                                render={({ field: { onBlur, onChange, value }, fieldState: { error, invalid } }) => (
                                    <Select
                                        disabled={isDefaultTemplate}
                                        error={invalid}
                                        fullWidth
                                        helperText={invalid ? error.message : null}
                                        label="Send from"
                                        onBlur={onBlur}
                                        onChange={onChange}
                                        value={value ?? ''}
                                        values={
                                            !isSenderProfilesLoading
                                                ? mapSenderProfilesToSelectValues(senderProfiles)
                                                : []
                                        }
                                    />
                                )}
                                rules={{
                                    required: 'please select a sender profile',
                                }}
                            />
                            <Controller
                                control={control}
                                name="toAddresses"
                                render={({ field: { onBlur, onChange, value }, fieldState: { error, invalid } }) => {
                                    const multipleAddressesHelper = 'use commas to separate multiple emails';

                                    return (
                                        <TextField
                                            data-testid="send-test-email-input"
                                            disabled={isDefaultTemplate}
                                            error={invalid}
                                            fullWidth
                                            helperText={invalid ? error.message : multipleAddressesHelper}
                                            label="Send to"
                                            onBlur={onBlur}
                                            onChange={onChange}
                                            value={value}
                                        />
                                    );
                                }}
                                rules={{
                                    required: 'please enter recipient email(s)',
                                    pattern: {
                                        value: /^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/,
                                        message: 'please enter recipient email(s) in valid format',
                                    },
                                }}
                            />
                            <Controller
                                control={control}
                                name="subjectLine"
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        disabled={isDefaultTemplate}
                                        fullWidth
                                        label="Subject line (optional)"
                                        onChange={onChange}
                                        value={value}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="preHeader"
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        data-testid="email-preview-text-input"
                                        disabled={isDefaultTemplate}
                                        fullWidth
                                        label="Preview text (optional)"
                                        onChange={onChange}
                                        value={value}
                                    />
                                )}
                            />
                            {hasDynamicContent && (
                                <Controller
                                    control={control}
                                    name="displayConditions"
                                    render={({ field: { onChange, value } }) => (
                                        <DisplayConditionOptionMultiSelect
                                            disabled={isDefaultTemplate}
                                            onChange={onChange}
                                            options={displayConditionOptions}
                                            selectedValues={value || []}
                                        />
                                    )}
                                />
                            )}
                        </Stack>
                    </form>
                    <DialogActions>
                        <Button data-testid="send-test-cancel-button" variant="text" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            data-testid="send-test-send-button"
                            disabled={isDefaultTemplate || isSendTestEmailLoading || !isValid}
                            variant="contained"
                            onClick={handleSubmit(onSubmit)}
                        >
                            Send
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default BeeEditorTestSendDialog;
